import React, { useEffect, useRef, useState } from "react";
import { useEditor } from "./EditorContext";

// Aba Conteúdo — Busca no topo, container #blocks abaixo (populado pelo GrapesJS).
// A busca filtra os blocos ocultando/exibindo pelo DOM. Usa MutationObserver
// pra reaplicar o filtro sempre que o GrapesJS re-renderiza (o BlockManager
// pode adicionar categorias/blocos assíncronos após o load inicial).

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

// Aplica o filtro no host — separado em função utilitária pra ser chamada
// tanto pelo MutationObserver quanto pelo effect que reage ao term.
// Usamos `setProperty("display", "none", "important")` porque o panels.scss
// tem `.gjs-block { display: flex !important }` — inline style sem !important
// perde pra regra da classe.
const applyBlockFilter = (host, rawTerm, setTotalVisible) => {
  if (!host) return;
  const q = normalize((rawTerm || "").trim());
  const allBlocks = host.querySelectorAll(".gjs-block");
  let visibleCount = 0;

  allBlocks.forEach((b) => {
    const labelEl = b.querySelector(".gjs-block-label");
    const label = normalize((labelEl?.textContent || "").trim());
    const titleAttr = normalize(b.getAttribute("title") || "");
    const fullText = normalize((b.textContent || "").trim());
    const match =
      !q ||
      label.includes(q) ||
      titleAttr.includes(q) ||
      fullText.includes(q);

    if (match) {
      b.style.removeProperty("display");
      visibleCount += 1;
    } else {
      b.style.setProperty("display", "none", "important");
    }
  });

  const cats = host.querySelectorAll(".gjs-block-category");
  cats.forEach((catEl) => {
    const visibleChildren = Array.from(
      catEl.querySelectorAll(".gjs-block"),
    ).filter((el) => el.style.display !== "none");
    const anyVisible = visibleChildren.length > 0;

    if (anyVisible) {
      catEl.style.removeProperty("display");
    } else {
      catEl.style.setProperty("display", "none", "important");
    }

    if (q && anyVisible) {
      const blocksC = catEl.querySelector(".gjs-blocks-c");
      if (blocksC) blocksC.style.removeProperty("display");
      catEl.classList.add("gjs-open");
    }
  });

  setTotalVisible(visibleCount);
};

const BlocksPanel = ({ active }) => {
  const { editor, isReady } = useEditor();
  const [term, setTerm] = useState("");
  const [totalVisible, setTotalVisible] = useState(0);
  const termRef = useRef("");

  useEffect(() => {
    termRef.current = term;
  }, [term]);

  // MutationObserver — reaplica o filtro sempre que #blocks muda no DOM.
  useEffect(() => {
    if (!isReady) return;
    const host = document.getElementById("blocks");
    if (!host) return;

    applyBlockFilter(host, termRef.current, setTotalVisible);

    const observer = new MutationObserver(() =>
      applyBlockFilter(host, termRef.current, setTotalVisible),
    );
    observer.observe(host, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isReady, editor]);

  // Reage à mudança do termo digitado.
  useEffect(() => {
    if (!isReady) return;
    const host = document.getElementById("blocks");
    applyBlockFilter(host, term, setTotalVisible);
  }, [term, isReady]);

  return (
    <div className={`v2-pane ${active ? "active" : ""}`} data-pane="content">
      <div className="v2-pane-header">
        <div className="v2-search">
          <i className="fa fa-search v2-search-icon" />
          <input
            type="text"
            placeholder="Buscar componente..."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
        {term && (
          <div
            style={{
              fontSize: 11,
              color: "#6b7280",
              marginTop: 6,
              paddingLeft: 2,
            }}
          >
            {totalVisible} resultado{totalVisible === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="v2-blocks-host">
        <div id="blocks" />
        {term && totalVisible === 0 && (
          <div className="v2-empty-blocks">
            <i className="fa fa-search" />
            <div>Nenhum componente encontrado para “{term}”</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlocksPanel;
